/**
 * CADINU Verified Deploy: constructor arguments. Every field is a plain string typed by the user; it is converted to the ABI type
 * strictly (no eval, no coercion of a wrong type), then encoded with viem's encodeAbiParameters, which validates it again.
 * Arrays and tuples are typed as JSON and parsed with the same size and depth guards as the artifact.
 */
import { encodeAbiParameters, getAddress, parseEther } from 'viem'
import type { AbiParam } from './artifact'
import { safeJsonParse } from './artifact'
import { ADDRESS_RE } from './bytecode'

export class ArgError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ArgError'
  }
}

const MAX_STRING = 10_000
const MAX_ARRAY = 1_000

export type FieldKind = 'address' | 'bool' | 'uint' | 'int' | 'bytes' | 'bytesN' | 'string' | 'json'
export function fieldKind(type: string): FieldKind {
  if (type.endsWith(']') || type.startsWith('tuple')) return 'json'
  if (type === 'address') return 'address'
  if (type === 'bool') return 'bool'
  if (type === 'string') return 'string'
  if (type === 'bytes') return 'bytes'
  if (type.startsWith('bytes')) return 'bytesN'
  if (type.startsWith('uint')) return 'uint'
  return 'int'
}

/** Human hint for a field, shown as placeholder text. */
export function fieldHint(p: AbiParam): string {
  const k = fieldKind(p.type)
  if (k === 'address') return '0x followed by 40 hex characters'
  if (k === 'bool') return 'true or false'
  if (k === 'uint' || k === 'int') return 'whole number in base units (decimal or 0x hex)'
  if (k === 'bytes') return '0x hex'
  if (k === 'bytesN') return `0x and exactly ${Number(p.type.slice(5)) * 2} hex characters`
  if (k === 'string') return 'text'
  return p.type.startsWith('tuple') ? 'JSON list of the tuple fields, in order' : 'JSON list, e.g. ["0x...", "0x..."]'
}

const bits = (type: string, prefix: 'uint' | 'int') => (type === prefix ? 256 : Number(type.slice(prefix.length)))

function toBigInt(v: unknown, label: string): bigint {
  if (typeof v === 'number') {
    if (!Number.isSafeInteger(v)) throw new ArgError(`${label}: the number is not a safe whole number; write it as a string.`)
    return BigInt(v)
  }
  if (typeof v !== 'string') throw new ArgError(`${label}: expected a whole number.`)
  const s = v.trim().replace(/_/g, '')
  if (/^-?[0-9]{1,80}$/.test(s)) return BigInt(s)
  if (/^0x[0-9a-fA-F]{1,64}$/.test(s)) return BigInt(s)
  throw new ArgError(`${label}: "${s.slice(0, 40)}" is not a whole number (decimal or 0x hex, no decimals, no exponent).`)
}

function convert(p: AbiParam, v: unknown, label: string): unknown {
  const t = p.type
  const arr = /^(.*)\[([0-9]*)\]$/.exec(t)
  if (arr) {
    if (!Array.isArray(v)) throw new ArgError(`${label}: expected a list.`)
    if (v.length > MAX_ARRAY) throw new ArgError(`${label}: more than ${MAX_ARRAY} items.`)
    if (arr[2] !== '' && v.length !== Number(arr[2])) throw new ArgError(`${label}: expected exactly ${arr[2]} items.`)
    const inner: AbiParam = { ...p, type: arr[1] }
    return v.map((x, i) => convert(inner, x, `${label}[${i}]`))
  }
  if (t === 'tuple') {
    const comps = p.components ?? []
    if (!Array.isArray(v)) throw new ArgError(`${label}: expected a list with the ${comps.length} tuple fields in order.`)
    if (v.length !== comps.length) throw new ArgError(`${label}: expected ${comps.length} tuple fields.`)
    return v.map((x, i) => convert(comps[i], x, `${label}.${comps[i].name || i}`))
  }
  if (t === 'address') {
    if (typeof v !== 'string' || !ADDRESS_RE.test(v.trim())) throw new ArgError(`${label}: not an address (0x and 40 hex characters).`)
    const a = v.trim()
    const mixed = a.slice(2) !== a.slice(2).toLowerCase() && a.slice(2) !== a.slice(2).toUpperCase()
    if (mixed && getAddress(a) !== a) throw new ArgError(`${label}: the address checksum is wrong (a typo?).`)
    return getAddress(a)
  }
  if (t === 'bool') {
    if (v === true || v === false) return v
    if (v === 'true') return true
    if (v === 'false') return false
    throw new ArgError(`${label}: expected true or false.`)
  }
  if (t === 'string') {
    if (typeof v !== 'string') throw new ArgError(`${label}: expected text.`)
    if (v.length > MAX_STRING) throw new ArgError(`${label}: longer than ${MAX_STRING} characters.`)
    return v
  }
  if (t === 'bytes' || t.startsWith('bytes')) {
    if (typeof v !== 'string' || !/^0x([0-9a-fA-F]{2})*$/.test(v.trim())) throw new ArgError(`${label}: expected 0x and an even number of hex characters.`)
    const h = v.trim().toLowerCase()
    if (t !== 'bytes' && (h.length - 2) / 2 !== Number(t.slice(5))) throw new ArgError(`${label}: ${t} needs exactly ${Number(t.slice(5))} bytes.`)
    return h
  }
  if (t.startsWith('uint')) {
    const n = toBigInt(v, label)
    if (n < 0n || n >= 2n ** BigInt(bits(t, 'uint'))) throw new ArgError(`${label}: out of range for ${t}.`)
    return n
  }
  if (t.startsWith('int')) {
    const n = toBigInt(v, label)
    const b = BigInt(bits(t, 'int'))
    if (n < -(2n ** (b - 1n)) || n >= 2n ** (b - 1n)) throw new ArgError(`${label}: out of range for ${t}.`)
    return n
  }
  throw new ArgError(`${label}: unsupported type ${t}.`)
}

/** One form field (string) to the value for encodeAbiParameters. */
export function parseField(p: AbiParam, raw: string, index: number): unknown {
  const label = p.name || `argument ${index + 1}`
  if (fieldKind(p.type) === 'json') {
    let v: unknown
    try {
      v = safeJsonParse(raw, 256 * 1024, 16)
    } catch {
      throw new ArgError(`${label}: not valid JSON.`)
    }
    return convert(p, v, label)
  }
  if (p.type === 'string') return convert(p, raw, label)
  return convert(p, raw.trim(), label)
}

export type EncodedArgs = { values: unknown[]; hex: string }

/** All fields to ABI-encoded constructor arguments (hex without 0x). Throws ArgError naming the first bad field. */
export function encodeArgs(inputs: AbiParam[], raws: string[]): EncodedArgs {
  if (raws.length !== inputs.length) throw new ArgError('Fill in every constructor argument.')
  const values = inputs.map((p, i) => parseField(p, raws[i] ?? '', i))
  if (!inputs.length) return { values, hex: '' }
  let hex: string
  try {
    hex = encodeAbiParameters(inputs as never, values as never)
  } catch (e) {
    throw new ArgError(`The arguments could not be encoded: ${String((e as Error)?.message ?? e).split('\n')[0].slice(0, 200)}`)
  }
  return { values, hex: hex.slice(2) }
}

/** A value to the text shown in the review (JSON-ish, bigint as decimal). Rendered as React text only. */
export function displayValue(v: unknown): string {
  if (typeof v === 'bigint') return v.toString()
  if (Array.isArray(v)) return `[${v.map(displayValue).join(', ')}]`
  if (typeof v === 'string') return v
  return String(v)
}

/** Parses the optional native-coin amount sent with a payable constructor (decimal, up to 18 places); empty = 0. */
export function parseValueNative(raw: string): bigint {
  const s = raw.trim()
  if (!s) return 0n
  if (!/^[0-9]{1,30}(\.[0-9]{1,18})?$/.test(s)) throw new ArgError('Value: a plain decimal amount, e.g. 0.01.')
  return parseEther(s as `${number}`)
}
